import { useEffect, useRef, useState } from "react";
import { Bot, Cpu, UserCheck, Zap, Ban, Link2, Anchor, ShieldCheck } from "lucide-react";
import { ProposePanel } from "./ProposePanel";
import { ACTIONS, COUNTERPARTIES, POLICY, evaluate, fmt } from "./lifecyclePolicy";

const C = { violet: "#8B5CF6", cyan: "#06B6D4", green: "#10B981", amber: "#F59E0B", red: "#F43F5E", slate: "#475569", dim: "#1E293B" };

const NODES = {
  agent: { x: 40, y: 180, w: 200, h: 100, title: "AI agent", sub: "proposes an action", Icon: Bot },
  policy: { x: 340, y: 150, w: 240, h: 160, title: "Policy engine", sub: "deterministic, pre-execution", Icon: Cpu },
  execute: { x: 940, y: 60, w: 220, h: 100, title: "Execute", sub: "within bound limits", Icon: Zap },
  human: { x: 700, y: 330, w: 260, h: 100, title: "Human approval gate", sub: "risk-calibrated oversight", Icon: UserCheck },
  blocked: { x: 360, y: 400, w: 200, h: 90, title: "Blocked", sub: "denied and recorded", Icon: Ban, dashed: true },
  tamper: { x: 60, y: 580, w: 320, h: 84, title: "Tamper-evident record", sub: "hash-linked to its predecessor", Icon: Link2, solid: true },
  anchor: { x: 440, y: 580, w: 320, h: 84, title: "External anchor", sub: "commitment the operator cannot rewrite", Icon: Anchor, solid: true },
  verify: { x: 820, y: 580, w: 320, h: 84, title: "Offline verification", sub: "regulator verifies on own hardware", Icon: ShieldCheck, solid: true, accent: true },
};

const EDGES = {
  propose: { d: "M240,230 L340,230", label: null },
  approved: { d: "M580,200 L760,200 L760,110 L940,110", label: { t: "approved", x: 670, y: 188 }, color: C.green },
  escalate: { d: "M580,260 L640,260 L640,380 L700,380", label: { t: "escalate", x: 655, y: 320, anchor: "start" }, color: C.amber },
  human: { d: "M960,380 L1050,380 L1050,160", label: { t: "human approves", x: 1062, y: 275, anchor: "start" }, color: C.green },
  deny: { d: "M460,310 L460,400", label: { t: "deny", x: 478, y: 360, anchor: "start" }, color: C.red },
  dropBlocked: { d: "M460,490 L460,540", drop: true },
  dropHuman: { d: "M830,430 L830,540", drop: true },
  dropExecute: { d: "M1120,160 L1120,540", drop: true },
  railBlocked: { d: "M460,540 L220,540 L220,580", rail: true },
  railHuman: { d: "M830,540 L220,540 L220,580", rail: true },
  railExecute: { d: "M1120,540 L220,540 L220,580", rail: true },
  chain1: { d: "M380,622 L440,622", rail: true },
  chain2: { d: "M760,622 L820,622", rail: true },
};

const RAIL = { x1: 40, x2: 1160, y: 540 };
const randomProposal = () => {
  const action = ACTIONS[(Math.random() * ACTIONS.length) | 0];
  const r = Math.random();
  const cp = r < 0.8 ? COUNTERPARTIES[(Math.random() * 3) | 0] : COUNTERPARTIES[3];
  const amount = r < 0.5 ? 200 + ((Math.random() * 4500) | 0) : r < 0.8 ? 5500 + ((Math.random() * 18000) | 0) : 1000 + ((Math.random() * 40000) | 0);
  return { action: action.id, amount, counterparty: cp.id };
};
const describe = (p) => `${ACTIONS.find((a) => a.id === p.action).verb} ${fmt(p.amount)} USDC → ${p.counterparty}`;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const hex = (n) => Array.from({ length: n }, () => "0123456789abcdef"[(Math.random() * 16) | 0]).join("");

export const GovernedLifecycle = ({ interactive = true }) => {
  const propose = (p) => {
    const mine = { ...p, mine: true };
    queue.current = [mine];
    setPending(mine);
  };
  const [status, setStatus] = useState({});
  const [edgeOn, setEdgeOn] = useState({});
  const [ledger, setLedger] = useState([]);
  const [evalText, setEvalText] = useState("awaiting proposal");
  const [pending, setPending] = useState(null);
  const [running, setRunning] = useState(null);
  const queue = useRef([]);
  const pathRefs = useRef({});
  const particle = useRef(null);
  const trail = useRef([]);
  const seq = useRef(1042);
  const prevHash = useRef(hex(8));
  const reduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    if (reduced) {
      setEdgeOn({ approved: 1, escalate: 1, human: 1, deny: 1 });
      return;
    }
    let alive = true;
    const set = (k, v) => alive && setStatus((s) => ({ ...s, [k]: v }));
    const say = (t) => alive && setEvalText(t);
    const edge = (k, v) => alive && setEdgeOn((s) => ({ ...s, [k]: v }));

    const moveParticle = (x, y, color) => {
      const p = particle.current;
      if (!p) return;
      p.setAttribute("transform", `translate(${x},${y})`);
      p.setAttribute("opacity", "1");
      p.querySelector("circle").setAttribute("fill", color);
      trail.current.unshift({ x, y });
      trail.current.length = Math.min(trail.current.length, 6);
      trail.current.forEach((pt, i) => {
        const t = document.getElementById(`gl-trail-${i}`);
        if (t) { t.setAttribute("cx", pt.x); t.setAttribute("cy", pt.y); t.setAttribute("fill", color); t.setAttribute("opacity", String(0.5 - i * 0.08)); }
      });
    };
    const hideParticle = () => {
      particle.current?.setAttribute("opacity", "0");
      for (let i = 0; i < 6; i++) document.getElementById(`gl-trail-${i}`)?.setAttribute("opacity", "0");
      trail.current = [];
    };
    const travel = (key, ms, color) =>
      new Promise((resolve) => {
        const el = pathRefs.current[key];
        if (!el) return resolve();
        const len = el.getTotalLength();
        const t0 = performance.now();
        const step = (now) => {
          if (!alive) return resolve();
          const k = Math.min(1, (now - t0) / ms);
          const e = k < 0.5 ? 2 * k * k : -1 + (4 - 2 * k) * k;
          const pt = el.getPointAtLength(e * len);
          moveParticle(pt.x, pt.y, color);
          k < 1 ? requestAnimationFrame(step) : resolve();
        };
        requestAnimationFrame(step);
      });

    const record = async (p, from, outcome, color) => {
      const dropKey = `drop${from}`, railKey = `rail${from}`;
      edge(dropKey, 1);
      await travel(dropKey, 450, C.cyan);
      edge(dropKey, 0);
      edge(railKey, 1);
      await travel(railKey, from === "Blocked" ? 600 : 900, C.cyan);
      edge(railKey, 0);
      hideParticle();
      set("tamper", "hit");
      const h = hex(8), prev = prevHash.current, id = seq.current++;
      prevHash.current = h;
      if (alive) setLedger((l) => [{ id, outcome, color, hash: h, prev, desc: describe(p), mine: p.mine }, ...l].slice(0, 4));
      await sleep(350);
      set("tamper", "idle");
      edge("chain1", 1); set("anchor", "hit");
      await sleep(350);
      edge("chain1", 0); set("anchor", "idle");
      edge("chain2", 1); set("verify", "hit");
      await sleep(450);
      edge("chain2", 0); set("verify", "idle");
    };

    const cycle = async () => {
      const p = queue.current.shift() || randomProposal();
      if (p.mine && alive) { setPending(null); setRunning(p); }
      const cp = COUNTERPARTIES.find((c) => c.id === p.counterparty);
      const { outcome, reason } = evaluate(p);
      set("agent", "active");
      say(`${p.mine ? "you propose" : "agent proposes"}: ${describe(p)}`);
      await sleep(p.mine ? 900 : 400);
      await travel("propose", 650, C.violet);
      set("agent", "idle");
      set("policy", "evaluating");
      hideParticle();
      const checks = [
        `allowlist: ${p.counterparty} ${cp?.allow ? "✓" : "✗"}`,
        `budget: $${fmt(p.amount)} of $${fmt(POLICY.dailyBudget)}/day ${p.amount > POLICY.dailyBudget ? "✗" : "✓"}`,
        `auto-approve limit: $${fmt(POLICY.autoApprove)} ${p.amount > POLICY.autoApprove ? "✗" : "✓"}`,
        "velocity cap ✓ · reasoning quality gate ✓",
      ];
      for (const c of checks) { say(c); await sleep(p.mine ? 520 : 300); }
      set("policy", outcome);
      if (outcome === "approved") {
        say(`verdict: APPROVED · ${reason}`);
        edge("approved", 1);
        await travel("approved", 1100, C.green);
        set("execute", "hit");
        await sleep(500);
        edge("approved", 0);
        await record(p, "Execute", "EXECUTE", C.green);
        set("execute", "idle");
      } else if (outcome === "escalate") {
        say(`verdict: ESCALATE · ${reason}`);
        edge("escalate", 1);
        await travel("escalate", 1000, C.amber);
        hideParticle();
        set("human", "reviewing");
        say("human reviewer evaluating context…");
        await sleep(1300);
        set("human", "hit");
        say("human approves · signature recorded");
        edge("escalate", 0);
        await record(p, "Human", "APPROVAL", C.amber);
        edge("human", 1);
        await travel("human", 900, C.green);
        set("execute", "hit");
        await sleep(400);
        edge("human", 0);
        set("human", "idle");
        await record(p, "Execute", "EXECUTE", C.green);
        set("execute", "idle");
      } else {
        say(`verdict: DENY · ${reason}`);
        edge("deny", 1);
        await travel("deny", 600, C.red);
        set("blocked", "hit");
        await sleep(450);
        edge("deny", 0);
        await record(p, "Blocked", "BLOCKED", C.red);
        set("blocked", "idle");
      }
      set("policy", "idle");
      say("awaiting proposal");
      if (p.mine && alive) setRunning(null);
      await sleep(queue.current.length ? 300 : 900);
    };

    (async () => { while (alive) await cycle(); })();
    return () => { alive = false; };
  }, [reduced]);

  return (
    <div className="relative" data-testid="governed-lifecycle">
      <div className="overflow-x-auto">
        <svg viewBox="0 0 1200 700" className="w-full min-w-[760px] h-auto select-none" role="img" aria-label="The governed action lifecycle: every proposed agent action is evaluated before execution; every outcome becomes verifiable evidence.">
          <defs>
            <filter id="gl-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="6" result="b" />
              <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
            <filter id="gl-soft" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="10" />
            </filter>
            <linearGradient id="gl-rail" x1="0" x2="1">
              <stop offset="0" stopColor={C.violet} stopOpacity="0.15" />
              <stop offset="0.5" stopColor={C.cyan} stopOpacity="0.6" />
              <stop offset="1" stopColor={C.violet} stopOpacity="0.15" />
            </linearGradient>
            <marker id="gl-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M0,0 L10,5 L0,10 z" fill="currentColor" />
            </marker>
          </defs>

          {Object.entries(EDGES).map(([k, e]) => {
            const on = edgeOn[k];
            const color = on ? e.color || C.cyan : e.drop ? C.slate : e.rail ? C.slate : "#334155";
            return (
              <g key={k} style={{ color, transition: "color 300ms" }}>
                <path ref={(el) => (pathRefs.current[k] = el)} d={e.d} fill="none" stroke={color} strokeWidth={on ? 2.5 : 1.5} strokeDasharray={e.drop ? "4 6" : undefined} strokeOpacity={e.drop && !on ? 0.5 : 1} markerEnd={e.drop || e.rail ? undefined : "url(#gl-arrow)"} style={{ transition: "stroke 300ms, stroke-width 300ms" }} />
                {on && !e.drop && !e.rail && <path d={e.d} fill="none" stroke={color} strokeWidth="8" strokeOpacity="0.25" filter="url(#gl-soft)" />}
                {e.label && (
                  <text x={e.label.x} y={e.label.y} textAnchor={e.label.anchor || "middle"} className="font-mono" fontSize="13" fontWeight="600" fill={on ? e.color : "#64748B"} style={{ transition: "fill 300ms" }}>
                    {e.label.t}
                  </text>
                )}
              </g>
            );
          })}

          <rect x={RAIL.x1} y={RAIL.y - 4} width={RAIL.x2 - RAIL.x1} height="8" rx="4" fill="url(#gl-rail)" />
          <text x="600" y={RAIL.y - 14} textAnchor="middle" className="font-mono" fontSize="12" fontWeight="600" fill={C.cyan} letterSpacing="1">
            EVERY PROPOSAL, DECISION, APPROVAL AND EXECUTION IS RECORDED
          </text>

          {Object.entries(NODES).map(([k, n]) => <Node key={k} id={k} n={n} status={status[k]} evalText={k === "policy" ? evalText : null} />)}

          {[5, 4, 3, 2, 1, 0].map((i) => <circle key={i} id={`gl-trail-${i}`} r={5 - i * 0.6} opacity="0" />)}
          <g ref={particle} opacity="0">
            <circle r="7" fill={C.violet} filter="url(#gl-glow)" />
            <circle r="3" fill="#fff" />
          </g>
        </svg>
      </div>

      {interactive && <ProposePanel onPropose={propose} pending={pending} running={running} />}

      <div className="mt-4 glass-card p-4 font-mono text-xs" data-testid="lifecycle-ledger">
        <div className="flex items-center justify-between mb-2 text-slate-500">
          <span className="uppercase tracking-[0.2em]">audit ledger · live</span>
          <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-swarm-green animate-pulse" />anchored</span>
        </div>
        <div className="space-y-1 min-h-[5.5rem]">
          {ledger.length === 0 && <div className="text-slate-600">waiting for first governed action…</div>}
          {ledger.map((r, i) => (
            <div key={r.id} className="flex items-center gap-3 text-slate-400 whitespace-nowrap" style={{ opacity: 1 - i * 0.22, animation: i === 0 ? "gl-slide 400ms ease-out" : undefined }}>
              <span className="text-slate-500">#{r.id}</span>
              <span className="font-semibold w-20 whitespace-nowrap" style={{ color: r.color }}>{r.outcome}</span>
              <span className="text-slate-300">{r.hash}</span>
              <span className="text-slate-600 hidden sm:inline">← {r.prev}</span>
              <span className="text-slate-500 hidden md:inline truncate">{r.desc}</span>
              {r.mine && <span className="ml-auto px-1.5 py-0.5 rounded bg-swarm-violet/20 text-swarm-violet text-[10px] font-bold tracking-wider" data-testid="ledger-mine-badge">YOU</span>}
            </div>
          ))}
        </div>
      </div>
      <style>{`@keyframes gl-slide { from { opacity: 0; transform: translateY(-6px); } to { transform: none; } } @keyframes gl-scan { to { stroke-dashoffset: -430; } }`}</style>
    </div>
  );
};

const STATUS_COLOR = { active: C.violet, evaluating: C.cyan, approved: C.green, escalate: C.amber, deny: C.red, hit: C.green, reviewing: C.amber };

const Node = ({ id, n, status, evalText }) => {
  const on = status && status !== "idle";
  let color = on ? STATUS_COLOR[status] : null;
  if (id === "blocked" && on) color = C.red;
  if (id === "tamper" || id === "anchor" || id === "verify") color = on ? C.cyan : null;
  const fill = n.solid ? (n.accent ? "#5B21B6" : "#0B1222") : "#0F172A";
  const stroke = color || (n.dashed ? "#475569" : n.solid ? "#1E3A5F" : "#334155");
  const cx = n.x + n.w / 2;
  const Icon = n.Icon;
  const badge = { evaluating: "EVALUATING", reviewing: "REVIEWING", hit: id === "blocked" ? "DENIED" : id === "execute" ? "EXECUTED" : id === "human" ? "APPROVED" : "RECORDED", approved: "APPROVED", escalate: "ESCALATE", deny: "DENY", active: "PROPOSING" }[status];
  return (
    <g data-testid={`lifecycle-node-${id}`}>
      {on && <rect x={n.x - 6} y={n.y - 6} width={n.w + 12} height={n.h + 12} rx="20" fill={color} opacity="0.18" filter="url(#gl-soft)" />}
      <rect x={n.x} y={n.y} width={n.w} height={n.h} rx="14" fill={fill} fillOpacity={n.solid ? 1 : 0.92} stroke={stroke} strokeWidth={on ? 2 : 1.5} strokeDasharray={n.dashed ? "6 5" : undefined} style={{ transition: "stroke 300ms, stroke-width 300ms" }} />
      <foreignObject x={n.x + 14} y={n.y + 14} width="28" height="28">
        <div style={{ color: color || (n.accent ? "#fff" : "#94A3B8"), transition: "color 300ms" }}><Icon size={22} /></div>
      </foreignObject>
      <text x={cx + 8} y={n.y + (evalText ? 40 : n.h / 2 - 6)} textAnchor="middle" fontSize="18" fontWeight="700" fill="#F8FAFC" style={{ fontFamily: "Manrope, system-ui, sans-serif" }}>{n.title}</text>
      <text x={cx + 8} y={n.y + (evalText ? 62 : n.h / 2 + 16)} textAnchor="middle" fontSize="13" fill={n.accent ? "#EDE9FE" : "#94A3B8"} style={{ fontFamily: "Manrope, system-ui, sans-serif" }}>{n.sub}</text>
      {evalText && (() => {
        const cut = evalText.length > 38 ? evalText.lastIndexOf(" ", 38) : -1;
        const l1 = cut > 0 ? evalText.slice(0, cut) : evalText;
        const l2 = cut > 0 ? evalText.slice(cut + 1) : "";
        return (
        <>
          <rect x={n.x + 14} y={n.y + 98} width={n.w - 28} height="44" rx="8" fill="#020617" stroke={color || "#1E293B"} strokeWidth="1" style={{ transition: "stroke 300ms" }} />
          <text x={cx} y={n.y + (l2 ? 116 : 124)} textAnchor="middle" fontSize="10.5" fill={color || "#64748B"} className="font-mono" style={{ transition: "fill 300ms" }}>{l1}</text>
          {l2 && <text x={cx} y={n.y + 131} textAnchor="middle" fontSize="10.5" fill={color || "#64748B"} className="font-mono">{l2}</text>}
          {status === "evaluating" && <rect x={n.x + 14} y={n.y + 98} width={n.w - 28} height="44" rx="8" fill="none" stroke={C.cyan} strokeWidth="1.5" strokeDasharray="30 400" style={{ animation: "gl-scan 1.2s linear infinite" }} />}
        </>
        );
      })()}
      {badge && (
        <g>
          <rect x={n.x + n.w - 96} y={n.y - 11} width="86" height="22" rx="11" fill={color} />
          <text x={n.x + n.w - 53} y={n.y + 4} textAnchor="middle" fontSize="10" fontWeight="700" fill="#020617" className="font-mono" letterSpacing="0.5">{badge}</text>
        </g>
      )}
    </g>
  );
};
